import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Modal from '../../components/Modal';
import { Badge, ProgressBar } from 'react-bootstrap';

// Helper function to format seconds to "0hrs 0mins 0secs" format
const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '0hrs 0mins 0secs';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs}hrs ${mins}mins ${secs}secs`;
};

const CombinedReportsPage = ({ pageTitle = 'View Reports' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Show the doctor progress panel to both medical admins and program admins
  const isAdminOrProgram = user && (
    (typeof user.role === 'string' && (user.role === 'medical_admin' || user.role === 'program_admin')) ||
    (user.role && user.role.toLowerCase && (user.role.toLowerCase().includes('medical_admin') || user.role.toLowerCase().includes('program_admin')))
  );
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [doctorsProgress, setDoctorsProgress] = useState({});
  const [doctorCasesDone, setDoctorCasesDone] = useState({}); // New: cases done per doctor per protocol
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [doctorProgressError, setDoctorProgressError] = useState(null);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingDoctorsProgress, setLoadingDoctorsProgress] = useState(false);
  const [showDoctorProgress, setShowDoctorProgress] = useState(false);
  const [showProtocolProgress, setShowProtocolProgress] = useState(true);
  const [doctorFilter, setDoctorFilter] = useState('active'); // 'all', 'active', 'inactive'

  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailedProgressData, setDetailedProgressData] = useState(null);
  const [loadingDetailedProgress, setLoadingDetailedProgress] = useState(false);

  // Load doctors assigned by this medical admin or all doctors for program_admin
  useEffect(() => {
    const loadAssignedDoctors = async () => {
      if (!user) return;
      setLoadingDoctors(true);
      try {
        const role = typeof user.role === 'string' ? user.role.toLowerCase() : (user.role?.name || '').toLowerCase();
        let docs = [];

        if (role === 'medical_admin' || role.includes('medical_admin')) {
          // Medical admins see doctors they assigned cases to
          const resp = await api.get(`/assignments/details?assigned_by=${user.id}&include_inactive=true`);
          const assignments = resp.data?.assignments || resp.data || [];
          const map = {};
          assignments.forEach(a => {
            const docId = a.doctor_id || a.doctorId || (a.doctor && a.doctor.id);
            const docName = a.doctor_name || (a.doctor && a.doctor.name) || `Doctor #${docId}`;
            const docEmail = a.doctor_email || (a.doctor && a.doctor.email) || '';
            const docIsActive = a.is_active !== false && !docEmail.startsWith('deleted_') && !docEmail.startsWith('delete_delete_');
            if (docId) {
              map[docId] = { id: docId, name: docName, email: docEmail, is_active: docIsActive };
            }
          });
          let doctors = Object.values(map);
          
          // Apply filter based on doctorFilter state
          // Also consider soft-deleted (email prefix) as inactive
          if (doctorFilter === 'active') {
            doctors = doctors.filter(d => d.is_active !== false && !d.email?.startsWith('deleted_') && !d.email?.startsWith('delete_delete_'));
          } else if (doctorFilter === 'inactive') {
            doctors = doctors.filter(d => d.is_active === false || d.email?.startsWith('deleted_') || d.email?.startsWith('delete_delete_'));
          }
          // 'all' shows all doctors
          
          docs = doctors;
        } else if (role === 'program_admin' || role.includes('program_admin')) {
          // Program admins see doctors based on doctorFilter
          const resp = await api.get('/doctors?include_inactive=true');
          let doctors = resp.data?.doctors || [];
          
          // Apply filter based on doctorFilter state
          // Also consider soft-deleted (email prefix) as inactive
          if (doctorFilter === 'active') {
            doctors = doctors.filter(d => d.is_active !== false && !d.email?.startsWith('deleted_') && !d.email?.startsWith('delete_delete_'));
          } else if (doctorFilter === 'inactive') {
            doctors = doctors.filter(d => d.is_active === false || d.email?.startsWith('deleted_') || d.email?.startsWith('delete_delete_'));
          }
          // 'all' shows all doctors
          
          docs = doctors.map(d => ({ id: d.id, name: d.name, email: d.email, is_active: d.is_active }));
        }

        setAssignedDoctors(docs);
        if (docs.length > 0) setSelectedDoctorId(docs[0].id);
      } catch (err) {
        console.error('Failed loading assigned doctors', err);
      } finally {
        setLoadingDoctors(false);
      }
    };

    loadAssignedDoctors();
  }, [user, doctorFilter]);

  // Load progress for all doctors only when section is expanded
  useEffect(() => {
    if (showDoctorProgress && assignedDoctors.length > 0 && Object.keys(doctorsProgress).length === 0) {
      loadAllDoctorsProgress();
    }
  }, [showDoctorProgress, assignedDoctors]);

  const loadAllDoctorsProgress = async () => {
    try {
      setLoadingDoctorsProgress(true);
      const resp = await api.get('/reports/doctors-progress');
      if (resp.data && resp.data.success) {
        setDoctorsProgress(resp.data.doctors_progress || {});
      } else {
        setDoctorsProgress({});
      }
      
      // Also load cases done data from new endpoint - DEPRECATED, using detailed endpoint now
      // try {
      //   const casesDoneResp = await api.get('/reports/doctor-cases-done');
      //   if (casesDoneResp.data && casesDoneResp.data.success) {
      //     // Convert array to lookup by doctor_id
      //     const casesDoneMap = {};
      //     (casesDoneResp.data.doctors || []).forEach(doc => {
      //       casesDoneMap[doc.doctor_id] = {};
      //       (doc.protocols || []).forEach(prot => {
      //         casesDoneMap[doc.doctor_id][prot.protocol_id] = prot.cases_done;
      //       });
      //     });
      //     setDoctorCasesDone(casesDoneMap);
      //     console.log('[CombinedReportsPage] Loaded cases done:', casesDoneMap);
      //   }
      // } catch (casesErr) {
      //   console.error('[CombinedReportsPage] Failed to load cases done:', casesErr);
      // }
    } catch (err) {
      console.error('Failed to load doctors progress', err);
      setDoctorsProgress({});
    } finally {
      setLoadingDoctorsProgress(false);
    }
  };

  const loadDoctorProgress = async (doctorId) => {
    if (!doctorId) return;
    try {
      setDoctorProgressError(null);
      const resp = await api.get(`/reports/doctor-progress?doctor_id=${doctorId}`);
      console.log('[CombinedReportsPage] doctor-progress response:', resp.data);
      if (resp.data && resp.data.success) {
        setDoctorsProgress(prev => ({ ...prev, [doctorId]: resp.data }));
      } else {
        setDoctorsProgress(prev => ({ ...prev, [doctorId]: null }));
        setDoctorProgressError(resp.data?.error || 'No progress data');
      }
    } catch (err) {
      console.error('[CombinedReportsPage] Failed to load doctor progress', err);
      setDoctorsProgress(prev => ({ ...prev, [doctorId]: null }));
      setDoctorProgressError(err.response?.data?.message || err.message || 'Request failed');
    }
  };

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const resp = await api.get('/reports');
      if (resp.data && resp.data.success) setReports(resp.data.reports || []);
      else setReports([]);
    } catch (err) {
      console.error('Failed to load reports', err);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const loadDetailedDoctorProgress = async (doctorId) => {
    if (!doctorId) return;
    setLoadingDetailedProgress(true);
    try {
      const response = await api.get(`/reports/doctor-detailed-progress-v3?doctor_id=${doctorId}`);
      const data = response.data;
      
      // Calculate total_cases_answered from batches - sum of all batch completed_cases
      if (data && data.batches && Array.isArray(data.batches)) {
        const totalCasesAnswered = data.batches.reduce((sum, batch) => {
          return sum + (batch.completed_cases || 0);
        }, 0);
        
        // Update the overall_stats with calculated value
        if (data.overall_stats) {
          data.overall_stats.total_cases_answered = totalCasesAnswered;
        }
      }
      
      setDetailedProgressData(data);
    } catch (err) {
      console.error('Error loading detailed progress:', err);
      setDetailedProgressData(null);
    } finally {
      setLoadingDetailedProgress(false);
    }
  };

  const handleShowMoreDetails = (doctorId) => {
    setSelectedDoctorId(doctorId);
    setShowDetailsModal(true);
    loadDetailedDoctorProgress(doctorId);
  };

  // Report cards data
  const reportCards = [
    {
      id: 'quality-answers',
      title: 'Quality of Answers Report',
      description: 'Review doctor answers per case with detailed case information and AI assessments',
      icon: 'fas fa-comments',
      color: 'primary',
      features: ['Case-by-case review', 'Doctor performance', 'AI assessment details', 'Side-by-side comparison']
    },
    {
      id: 'time-analytics',
      title: 'Time Analytics',
      description: 'Analyze time spent by doctors on cases, protocols, and batches',
      icon: 'fas fa-clock',
      color: 'info',
      features: ['Time tracking', 'Doctor efficiency', 'Protocol analysis', 'Batch performance']
    },
    {
      id: 'statistical',
      title: 'Statistical Reports',
      description: 'Comprehensive statistical analysis and research reports',
      icon: 'fas fa-chart-bar',
      color: 'success',
      features: ['Data analysis', 'Research insights', 'Custom reports', 'Export capabilities']
    }
  ];

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    if (percentage >= 40) return 'info';
    return 'danger';
  };

  const getCompletionIcon = (percentage) => {
    if (percentage >= 100) return 'fas fa-check-circle';
    if (percentage >= 80) return 'fas fa-thumbs-up';
    if (percentage >= 60) return 'fas fa-clock';
    if (percentage >= 40) return 'fas fa-hourglass-half';
    return 'fas fa-exclamation-triangle';
  };

  // Render overview page
  const renderOverview = () => (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-chart-line text-primary me-2"></i>
          All Reports Overview
        </h2>
        <button
          className="btn btn-outline-primary"
          onClick={loadReports}
          disabled={loadingReports}
        >
          <i className="fas fa-sync-alt me-2"></i>
          Refresh
        </button>
      </div>

      {/* Report Type Cards */}
      <div className="row mb-5">
        {reportCards.map((report) => (
          <div key={report.id} className="col-lg-4 mb-4">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-header bg-light">
                <div className="d-flex align-items-center">
                  <div className={`bg-${report.color} text-white rounded-circle p-2 me-3`}>
                    <i className={`${report.icon} fa-lg`}></i>
                  </div>
                  <div>
                    <h5 className="mb-0">{report.title}</h5>
                    <small className="text-muted">Medical Administration</small>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <p className="card-text text-muted mb-3">{report.description}</p>
                <div className="mb-3">
                  <strong className="text-sm">Features:</strong>
                  <ul className="list-unstyled mt-2">
                    {report.features.map((feature, index) => (
                      <li key={index} className="small text-muted">
                        <i className="fas fa-check text-success me-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="card-footer bg-white border-0">
                <Link
                  to={`/medical-admin/${report.id === 'quality-answers' ? 'quality-of-answers' : report.id === 'time-analytics' ? 'time-analytics' : 'reports'}`}
                  className={`btn btn-${report.color} w-100`}
                >
                  <i className="fas fa-arrow-right me-2"></i>
                  View {report.title}
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Doctor Progress Section */}
      {isAdminOrProgram && (
        <div className="row mb-5">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-user-md text-primary me-2"></i>
                  Doctor Work Progress
                </h5>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowDoctorProgress(!showDoctorProgress)}
                >
                  <i className={`fas fa-chevron-${showDoctorProgress ? 'up' : 'down'} me-2`}></i>
                  {showDoctorProgress ? 'Hide' : 'Show'} Progress
                </button>
                {(user?.role === 'program_admin' || user?.role === 'medical_admin' || user?.role?.toLowerCase()?.includes('program_admin') || user?.role?.toLowerCase()?.includes('medical_admin')) && (
                  <div className="btn-group">
                    <button
                      className={`btn btn-sm btn-outline-secondary dropdown-toggle`}
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="fas fa-filter me-1"></i>
                      {doctorFilter === 'active' ? 'Active' : doctorFilter === 'inactive' ? 'Inactive' : 'All'} Doctors
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button
                          className={`dropdown-item ${doctorFilter === 'active' ? 'active' : ''}`}
                          onClick={() => setDoctorFilter('active')}
                        >
                          <i className="fas fa-user-check me-2"></i>Active Only
                        </button>
                      </li>
                      <li>
                        <button
                          className={`dropdown-item ${doctorFilter === 'inactive' ? 'active' : ''}`}
                          onClick={() => setDoctorFilter('inactive')}
                        >
                          <i className="fas fa-user-slash me-2"></i>Inactive Only
                        </button>
                      </li>
                      <li>
                        <button
                          className={`dropdown-item ${doctorFilter === 'all' ? 'active' : ''}`}
                          onClick={() => setDoctorFilter('all')}
                        >
                          <i className="fas fa-users me-2"></i>All Doctors
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              {showDoctorProgress && (
                <div className="card-body">
                {loadingDoctors ? (
                  <div className="text-center py-3">Loading doctors...</div>
                ) : assignedDoctors.length === 0 ? (
                  <div className="alert alert-secondary">No doctors assigned yet.</div>
                ) : (
                  loadingDoctorsProgress ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading doctors progress...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading doctor progress data...</p>
                    </div>
                  ) : (
                    <div className="row">
                      {assignedDoctors.map((doctor) => (
                        <div key={doctor.id} className="col-lg-6 mb-4">
                          <div className="card border">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                              <h6 className="mb-0">
                                <i className="fas fa-user-md text-primary me-2"></i>
                                Dr. {doctor.name}
                                {doctor.is_active === false && (
                                  <span className="badge bg-secondary ms-2">Inactive</span>
                                )}
                              </h6>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleShowMoreDetails(doctor.id)}
                              >
                                <i className="fas fa-info-circle me-1"></i>
                                Details
                              </button>
                            </div>
                            <div className="card-body">
                              {doctorsProgress[doctor.id] ? (
                                <div>
                                  <div className="row g-2 mb-3">
                                    <div className="col-6">
                                      <div className="text-center">
                                        <div className="small text-muted">Cases</div>
                                        <div className="fw-bold">{doctorsProgress[doctor.id].total_cases || 0}</div>
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <div className="text-center">
                                        <div className="small text-muted">Questions</div>
                                        <div className="fw-bold">{doctorsProgress[doctor.id].total_assigned_questions || 0}</div>
                                      </div>
                                    </div>
                                  </div>

                                  {(() => {
                                    const progress = doctorsProgress[doctor.id];
                                    // Calculate progress based on questions assigned vs answered
                                    const totalAssigned = progress.total_assigned_questions || 0;
                                    const totalAnswered = progress.total_answered_questions || 0;
                                    const questionProgress = totalAssigned > 0 ? Math.round((totalAnswered / totalAssigned) * 100) : 0;
                                    
                                    return (
                                      <div>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <small className="text-muted">Progress</small>
                                          <small className="text-muted">{questionProgress}%</small>
                                        </div>
                                        <div className="progress" style={{ height: '6px' }}>
                                          <div className="progress-bar bg-warning" style={{ width: `${questionProgress}%` }} />
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                <div className="text-center py-3">
                                  <i className="fas fa-chart-line fa-2x text-muted mb-2"></i>
                                  <p className="text-muted small mb-0">No progress data available</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistical Reports Section */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                <i className="fas fa-chart-bar text-success me-2"></i>
                Available Statistical Reports
              </h5>
            </div>
            <div className="card-body">
              {loadingReports ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading reports...</span>
                  </div>
                  <p className="mt-2">Loading statistical reports...</p>
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                  <h5>No statistical reports found</h5>
                  <p className="text-muted mb-0">Statistical reports will appear here when created by statisticians.</p>
                </div>
              ) : (
                <div className="row">
                  {reports.slice(0, 6).map(report => (
                    <div key={report.id} className="col-md-6 col-lg-4 mb-4">
                      <div className="card h-100 border">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                          <span className={`badge bg-${report.report_type === 'statistical' ? 'primary' : report.report_type === 'research' ? 'success' : report.report_type === 'clinical' ? 'warning' : 'info'}`}>{report.report_type}</span>
                          <small className="text-muted">{new Date(report.created_at).toLocaleDateString()}</small>
                        </div>
                        <div className="card-body">
                          <h6 className="card-title">{report.title}</h6>
                          <p className="card-text text-muted small mb-2">{report.description && report.description.length > 100 ? `${report.description.substring(0,100)}...` : report.description}</p>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted"><i className="fas fa-user me-1"></i>{report.created_by_name || 'Statistician'}</small>
                            <span className="badge bg-light text-dark">{report.data_points || '0'} data points</span>
                          </div>
                        </div>
                        <div className="card-footer bg-white">
                          <Link to={`/medical-admin/reports/${report.id}`} className="btn btn-outline-primary btn-sm d-block">
                            <i className="fas fa-chart-bar me-1"></i>View Report
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                  {reports.length > 6 && (
                    <div className="col-12 text-center mt-3">
                      <Link to="/medical-admin/reports" className="btn btn-outline-secondary">
                        <i className="fas fa-list me-2"></i>
                        View All {reports.length} Reports
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ResearchLayout pageTitle={pageTitle}>
      {renderOverview()}

      {/* Detailed Progress Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={`Detailed Progress Report - ${assignedDoctors.find(d => d.id === selectedDoctorId)?.name || 'Doctor'}`}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loadingDetailedProgress ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading detailed progress...</p>
            </div>
          ) : detailedProgressData ? (
            <div>
              {/* Overall Statistics */}
              <div className="mb-4">
                <h6 className="mb-3">Overall Statistics</h6>
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <div className="text-primary mb-2">
                          <i className="fas fa-clipboard-list fa-2x"></i>
                        </div>
                        <h5 className="fw-bold text-primary">{detailedProgressData.overall_stats?.total_protocols || 0}</h5>
                        <p className="text-muted mb-0 small">Total Protocols</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <div className="text-success mb-2">
                          <i className="fas fa-users fa-2x"></i>
                        </div>
                        <h5 className="fw-bold text-success">
                          {detailedProgressData.overall_stats?.total_cases_answered || 0}/{detailedProgressData.overall_stats?.total_cases || 0}
                        </h5>
                        <p className="text-muted mb-0 small">Cases Answered</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <div className="text-info mb-2">
                          <i className="fas fa-question-circle fa-2x"></i>
                        </div>
                        <h5 className="fw-bold text-info">
                          {detailedProgressData.overall_stats?.total_questions_answered || 0}/{detailedProgressData.overall_stats?.total_questions_available || 0}
                        </h5>
                        <p className="text-muted mb-0 small">Questions Answered</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <div className="text-warning mb-2">
                          <i className="fas fa-clock fa-2x"></i>
                        </div>
                        <h5 className="fw-bold text-warning">
                          {formatTime((detailedProgressData.overall_stats?.total_time_spent_minutes || 0) * 60)}
                        </h5>
                        <p className="text-muted mb-0 small">Time Taken</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <div className={`text-${getCompletionColor(detailedProgressData.overall_stats?.overall_completion_percentage || 0)} mb-2`}>
                          <i className={`${getCompletionIcon(detailedProgressData.overall_stats?.overall_completion_percentage || 0)} fa-2x`}></i>
                        </div>
                        <h5 className={`fw-bold text-${getCompletionColor(detailedProgressData.overall_stats?.overall_completion_percentage || 0)}`}>
                          {detailedProgressData.overall_stats?.overall_completion_percentage || 0}%
                        </h5>
                        <p className="text-muted mb-0 small">Overall Completion</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* =============================================================================== */}
              {/* ============================= OLD BATCH CARD (COMMENTED OUT) ============================= */}
              {/* =============================================================================== */}
              {/*
              {detailedProgressData.batches && detailedProgressData.batches.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3">Batch Progress Details</h6>
                  <div className="row">
                    {detailedProgressData.batches.map((batch, index) => (
                      <div key={batch.batch_id} className="col-12 mb-3">
                        <div className="card border">
                          <div className="card-header bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0 fw-bold">{batch.batch_name}</h6>
                              <div>
                                <span className={`badge bg-${batch.is_active ? 'success' : batch.is_expired ? 'danger' : 'secondary'} me-2`}>
                                  {batch.status}
                                </span>
                                <span className={`badge bg-${getCompletionColor(batch.completion_percentage)}`}>
                                  {batch.completion_percentage}%
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="card-body">
                            {batch.batch_description && (
                              <p className="text-muted small mb-3">{batch.batch_description}</p>
                            )}

                            <div className="row mb-3">
                              <div className="col-3">
                                <div className="text-center">
                                  <div className="h6 text-primary mb-1">{batch.total_cases}</div>
                                  <div className="small text-muted">Total Cases</div>
                                </div>
                              </div>
                              <div className="col-3">
                                <div className="text-center">
                                  <div className="h6 text-success mb-1">{batch.completed_cases}</div>
                                  <div className="small text-muted">Completed</div>
                                </div>
                              </div>
                              <div className="col-3">
                                <div className="text-center">
                                  <div className="h6 text-info mb-1">{batch.total_questions}</div>
                                  <div className="small text-muted">Total Questions</div>
                                </div>
                              </div>
                              <div className="col-3">
                                <div className="text-center">
                                  <div className="h6 text-warning mb-1">{batch.answered_questions}</div>
                                  <div className="small text-muted">Answered</div>
                                </div>
                              </div>
                            </div>

                            <div className="row mb-3">
                              <div className="col-6">
                                <div className="small text-muted">Start Date</div>
                                <div>{batch.start_date ? new Date(batch.start_date).toLocaleDateString() : 'N/A'}</div>
                              </div>
                              <div className="col-6">
                                <div className="small text-muted">End Date</div>
                                <div>{batch.end_date ? new Date(batch.end_date).toLocaleDateString() : 'N/A'}</div>
                              </div>
                            </div>

                            {batch.time_remaining && (
                              <div className="mb-3">
                                <div className="small text-muted">Time Remaining</div>
                                <div className="text-warning fw-bold">{batch.time_remaining}</div>
                              </div>
                            )}

                            <div className="progress mb-2" style={{ height: '8px' }}>
                              <div
                                className={`progress-bar bg-${getCompletionColor(batch.completion_percentage)}`}
                                role="progressbar"
                                style={{ width: `${batch.completion_percentage}%` }}
                                aria-valuenow={batch.completion_percentage}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>

                            <div className="d-flex justify-content-between small text-muted">
                              <span>
                                {batch.cases_started || batch.completed_cases} of {batch.total_cases} cases completed
                              </span>
                              <span>{batch.completion_percentage}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              */}
              {/* =========================== END OLD BATCH CARD =========================== */}

              {/* ============================= NEW ENHANCED BATCH CARD ============================= */}
              {/* This section shows the selected doctor's progress within each batch */}
              {detailedProgressData.batches && detailedProgressData.batches.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3">
                    <i className="fas fa-user-md me-2"></i>
                    Doctor's Progress by Batch
                  </h6>
                  <div className="row">
                    {detailedProgressData.batches.map((batch) => (
                      <div key={batch.batch_id} className="col-12 mb-4">
                        <div className="card border">
                          {/* Batch Header - Doctor's Progress in this Batch */}
                          <div className="card-header bg-primary text-white">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h5 className="mb-0 fw-bold">
                                  <i className="fas fa-layer-group me-2"></i>
                                  {batch.batch_name}
                                </h5>
                                {batch.batch_description && (
                                  <p className="mb-0 small opacity-75">{batch.batch_description}</p>
                                )}
                                <p className="mb-0 small opacity-75 mt-1">
                                  <i className="fas fa-user-md me-1"></i>
                                  Doctor's Progress
                                </p>
                              </div>
                              <div className="d-flex flex-column align-items-end gap-2">
                                <span className={`badge bg-${batch.is_active ? 'success' : batch.is_expired ? 'danger' : 'secondary'} fs-6`}>
                                  <i className={`fas ${batch.is_active ? 'fa-check-circle' : batch.is_expired ? 'fa-times-circle' : 'fa-clock'} me-1`}></i>
                                  {batch.is_active ? 'Active' : batch.is_expired ? 'Expired' : batch.status}
                                </span>
                                {batch.time_remaining && (
                                  <span className="badge bg-warning text-dark fs-6">
                                    <i className="fas fa-clock me-1"></i>
                                    {batch.time_remaining}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="card-body">
                            {/* Overall Batch Statistics */}
                            <div className="row mb-4">
                              <div className="col-md-3">
                                <div className="card bg-light h-100">
                                  <div className="card-body text-center">
                                    <div className="text-primary mb-2">
                                      <i className="fas fa-folder-open fa-2x"></i>
                                    </div>
                                    <h4 className="fw-bold text-primary mb-0">{batch.total_cases_in_batch}</h4>
                                    <p className="text-muted mb-0 small">Total Cases Assigned to Batch</p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="card bg-light h-100">
                                  <div className="card-body text-center">
                                    <div className="text-success mb-2">
                                      <i className="fas fa-check-circle fa-2x"></i>
                                    </div>
                                    <h4 className="fw-bold text-success mb-0">{batch.completed_cases}</h4>
                                    <p className="text-muted mb-0 small">Cases Answered by Doctor</p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="card bg-light h-100">
                                  <div className="card-body text-center">
                                    <div className="text-info mb-2">
                                      <i className="fas fa-clipboard-list fa-2x"></i>
                                    </div>
                                    <h4 className="fw-bold text-info mb-0">{batch.protocols?.length || batch.total_protocols || 0}</h4>
                                    <p className="text-muted mb-0 small">Protocols in Batch</p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="card bg-light h-100">
                                  <div className="card-body text-center">
                                    <div className="text-warning mb-2">
                                      <i className="fas fa-hourglass-half fa-2x"></i>
                                    </div>
                                    <h4 className="fw-bold text-warning mb-0">{batch.pending_cases || (batch.assigned_cases - batch.completed_cases)}</h4>
                                    <p className="text-muted mb-0 small">Cases Remaining</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Batch Timeline */}
                            <div className="row mb-4">
                              <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <i className="fas fa-calendar-plus text-success fa-lg"></i>
                                  </div>
                                  <div>
                                    <div className="text-muted small">Start Date</div>
                                    <div className="fw-bold">
                                      {batch.start_date ? new Date(batch.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <i className={`fas fa-calendar-check ${batch.is_expired ? 'text-danger' : 'text-warning'} fa-lg`}></i>
                                  </div>
                                  <div>
                                    <div className="text-muted small">End Date / Deadline</div>
                                    <div className={`fw-bold ${batch.is_expired ? 'text-danger' : ''}`}>
                                      {batch.end_date ? new Date(batch.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <i className="fas fa-stopwatch text-primary fa-lg"></i>
                                  </div>
                                  <div>
                                    <div className="text-muted small">Time Remaining</div>
                                    <div className={`fw-bold ${batch.is_expired ? 'text-danger' : batch.time_remaining?.includes('hour') || batch.time_remaining?.includes('day') ? 'text-warning' : 'text-success'}`}>
                                      {batch.is_expired ? 'Expired' : batch.time_remaining || 'N/A'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Batch Progress Bar */}
                            <div className="mb-4">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-bold">Batch Progress</span>
                                <span className="badge bg-primary fs-6">{batch.completion_percentage}%</span>
                              </div>
                              <div className="progress" style={{ height: '12px' }}>
                                <div
                                  className={`progress-bar bg-${getCompletionColor(batch.completion_percentage)}`}
                                  role="progressbar"
                                  style={{ width: `${batch.completion_percentage}%` }}
                                  aria-valuenow={batch.completion_percentage}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                >
                                </div>
                              </div>
                               <div className="d-flex justify-content-between small text-muted mt-1">
                                 <span>{batch.cases_started || batch.completed_cases} of {batch.total_cases} cases answered</span>
                                 <span>{batch.total_cases - (batch.cases_started || batch.completed_cases)} remaining</span>
                               </div>
                            </div>

                            {/* Protocols Section */}
                            {batch.protocols && batch.protocols.length > 0 && (
                              <div className="mt-4">
                                <h6 className="mb-3">
                                  <i className="fas fa-list-ol me-2"></i>
                                  Protocols in this Batch
                                </h6>
                                <div className="table-responsive">
                                  <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                      <tr>
                                        <th className="text-center" style={{ width: '40px' }}>#</th>
                                        <th>Protocol Name</th>
                                        <th className="text-center">Questions per Protocol</th>
                                        <th className="text-center">Cases Done</th>
                                        <th className="text-center">Total Questions</th>
                                        <th className="text-center">Total Answers</th>
                                        <th className="text-center">Progress</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {batch.protocols.map((protocol, index) => (
                                        <tr key={protocol.protocol_id || index}>
                                          <td className="text-center text-muted">{index + 1}</td>
                                          <td>
                                            <div className="fw-bold">
                                              <i className="fas fa-clipboard-check text-primary me-2"></i>
                                              {protocol.protocol_name}
                                            </div>
                                          </td>
                                          <td className="text-center">
                                            <Badge bg="info">{protocol.questions_per_case || protocol.total_questions || 0}</Badge>
                                          </td>
                                          <td className="text-center">
                                            <Badge bg="warning">
                                              {/* Show just cases_done without the total */}
                                              {protocol.cases_done !== undefined 
                                                ? protocol.cases_done 
                                                : (doctorCasesDone[selectedDoctorId]?.[protocol.protocol_id] || protocol.cases_started || 0)}
                                            </Badge>
                                          </td>
                                          <td className="text-center">
                                            <Badge bg="primary">{protocol.total_questions || 0}</Badge>
                                          </td>
                                          <td className="text-center">
                                            <Badge bg="success">{protocol.answered_questions || protocol.completed_questions || 0}</Badge>
                                          </td>
                                          <td className="text-center" style={{ width: '150px' }}>
                                            <div className="d-flex align-items-center">
                                              <ProgressBar
                                                now={protocol.completion_percentage || 0}
                                                variant={getCompletionColor(protocol.completion_percentage || 0)}
                                                className="flex-grow-1 me-2"
                                                style={{ height: '8px' }}
                                              />
                                              <span className="small fw-bold">{protocol.completion_percentage || 0}%</span>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Questions Summary - Doctor's Progress in this Batch */}
                            <div className="row mt-4">
                              <div className="col-md-6">
                                <div className={`card bg-primary bg-opacity-10 border-primary ${batch.answered_questions > batch.total_questions ? 'border-danger' : ''}`}>
                                  <div className="card-body text-center">
                                    {/* Use backend's total_questions directly */}
                                    {(() => {
                                      const displayTotalQuestions = batch.total_questions || 0;
                                      
                                      return (
                                        <>
                                          <h3 className="fw-bold text-primary mb-0">{displayTotalQuestions}</h3>
                                          <p className="text-muted mb-0 small">Total Questions in Batch</p>
                                          {batch.answered_questions > displayTotalQuestions && (
                                            <div className="text-danger small mt-1">
                                              <i className="fas fa-exclamation-triangle me-1"></i>
                                              Doctor Answered: {batch.answered_questions}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className={`card bg-success bg-opacity-10 border-success ${batch.answered_questions > (batch.total_questions || 0) ? 'border-danger' : ''}`}>
                                  <div className="card-body text-center">
                                    {/* Cap answered at total to avoid showing invalid data */}
                                    <h3 className="fw-bold text-success mb-0">
                                      {Math.min(batch.answered_questions || 0, batch.total_questions || 0)}
                                    </h3>
                                    <p className="text-muted mb-0 small">Doctor's Answers Given</p>
                                    {batch.answered_questions > (batch.total_questions || 0) && (
                                      <div className="text-danger small mt-1">
                                        <i className="fas fa-exclamation-triangle me-1"></i>
                                        Data mismatch (backend: {batch.answered_questions})
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* =========================== END NEW ENHANCED BATCH CARD =========================== */}

              {/* Batch Totals (Doctor's Batches) */}
              {detailedProgressData.batch_totals && detailedProgressData.batch_totals.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3">Batch Totals (All Active Batches)</h6>
                  <div className="row">
                    {detailedProgressData.batch_totals.map((batch) => (
                      <div key={batch.batch_id} className="col-md-4 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body text-center">
                            <h6 className="fw-bold text-primary mb-2">{batch.batch_name}</h6>
                            <div className="row">
                              <div className="col-6">
                                <h5 className="fw-bold text-info mb-0">{batch.total_cases_in_batch}</h5>
                                <small className="text-muted">Total Cases</small>
                              </div>
                              <div className="col-6">
                                <h5 className="fw-bold text-success mb-0">{batch.total_questions_in_batch}</h5>
                                <small className="text-muted">Total Questions</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Protocol Details */}
              {detailedProgressData.protocols && detailedProgressData.protocols.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3">Protocol Progress Details</h6>
                  <div className="row">
                    {detailedProgressData.protocols.map((protocol, index) => (
                      <div key={protocol.protocol_id} className="col-12 mb-3">
                        <div className="card border">
                          <div className="card-header bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0 fw-bold">{protocol.protocol_name}</h6>
                              <span className={`badge bg-${getCompletionColor(protocol.completion_percentage)}`}>
                                {protocol.completion_percentage}%
                              </span>
                            </div>
                          </div>
                          <div className="card-body">
                            {protocol.protocol_description && (
                              <p className="text-muted small mb-3">{protocol.protocol_description}</p>
                            )}

                            <div className="row mb-3">
                              <div className="col-4">
                                <div className="text-center">
                                  <div className="h6 text-primary mb-1">{protocol.total_cases}</div>
                                  <div className="small text-muted">Total Cases</div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="text-center">
                                  <div className="h6 text-info mb-1">{protocol.total_questions}</div>
                                  <div className="small text-muted">Questions per Protocol</div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="text-center">
                                  <div className="h6 text-success mb-1">{protocol.answered_questions}</div>
                                  <div className="small text-muted">Answered</div>
                                </div>
                              </div>
                            </div>

                            <div className="progress mb-2" style={{ height: '8px' }}>
                              <div
                                className={`progress-bar bg-${getCompletionColor(protocol.completion_percentage)}`}
                                role="progressbar"
                                style={{ width: `${protocol.completion_percentage}%` }}
                                aria-valuenow={protocol.completion_percentage}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>

                            <div className="d-flex justify-content-between small text-muted">
                              <span>
                                {protocol.answered_questions} of {protocol.total_questions} questions answered
                              </span>
                              <span>{protocol.completion_percentage}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Completed Assignments */}
              {detailedProgressData.completed_assignments && detailedProgressData.completed_assignments.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3">Recent Completed Assignments</h6>
                  <div className="table-responsive">
                    <table className="table table-hover table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Protocol</th>
                          <th>Case ID</th>
                          <th>Questions</th>
                          <th>Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedProgressData.completed_assignments.slice(0, 10).map((assignment, index) => (
                          <tr key={index}>
                            <td className="fw-bold small">{assignment.protocol_name}</td>
                            <td>
                              <span className="badge bg-primary">{assignment.case_id}</span>
                            </td>
                            <td>
                              <span className="badge bg-success">{assignment.questions_answered}</span>
                            </td>
                            <td className="text-muted small">
                              {assignment.completed_at ? new Date(assignment.completed_at).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <i className="fas fa-exclamation-triangle fa-3x text-muted mb-3"></i>
              <h6 className="text-muted">No detailed progress data available</h6>
              <p className="text-muted mb-0">Unable to load detailed progress information for this doctor.</p>
            </div>
          )}
        </div>
      </Modal>
    </ResearchLayout>
  );
};

export default CombinedReportsPage;

